/**
 * ItemIntegrationsModal
 *
 * Integrations used to be the sixth of eight tabs on the item detail page —
 * a permanent slot in the primary navigation for something a user opens
 * occasionally. It is now a header action that opens this modal.
 *
 * Two things live here:
 *
 *   1. A roster of the integrations this item can be linked to. Each row
 *      resolves the workspace's connector config, so a row only offers a link
 *      out to the external system when that connector is actually configured
 *      AND this item has an external URL recorded for it. Anything else reads
 *      as unavailable — no dead links.
 *
 *   2. The link/sync panels themselves (Jira, Linear, Quantive, Granola,
 *      Figma), so everything reachable from the old tab stays reachable.
 */

import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Loader2, Plug, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  resolveConnectorStatuses,
  type ConnectorStatus,
} from '@/lib/connectorConfigsService';
import {
  getConnectorDefinition,
  getExternalIds,
  type ConnectorType,
} from '@/lib/connectors';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { useUserStore } from '@/stores/prioritization/userStore';
import { ItemJiraSyncPanel } from './ItemJiraSyncPanel';
import { ItemLinearSyncPanel } from './ItemLinearSyncPanel';
import { ItemQuantiveSyncPanel } from './ItemQuantiveSyncPanel';
import { ItemGranolaSyncPanel } from './ItemGranolaSyncPanel';
import { ItemFigmaSyncPanel } from './ItemFigmaSyncPanel';

/** The connectors an item can be linked to from this page. */
const ITEM_CONNECTORS: ConnectorType[] = ['jira', 'linear', 'quantive', 'granola', 'figma'];

interface ExternalLinkRow {
  connectorType: string;
  externalId: string;
  /** The key a person recognizes (PRI-12) when externalId is an opaque id. */
  externalLabel: string | null;
  externalUrl: string | null;
}

interface IntegrationRow {
  type: ConnectorType;
  name: string;
  description: string;
  status: ConnectorStatus;
  links: ExternalLinkRow[];
}

export interface ItemIntegrationsModalProps {
  itemId: string;
  itemTitle?: string;
  onClose: () => void;
}

export function ItemIntegrationsModal({ itemId, itemTitle, onClose }: ItemIntegrationsModalProps) {
  const { userId } = useUserStore();
  const [rows, setRows] = useState<IntegrationRow[] | null>(null);

  const load = useCallback(async () => {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) {
      setRows([]);
      return;
    }

    const [statuses, externalIds] = await Promise.all([
      resolveConnectorStatuses(workspaceId, userId ?? null, ITEM_CONNECTORS),
      getExternalIds(workspaceId, 'item', itemId),
    ]);

    setRows(
      ITEM_CONNECTORS.map((type) => {
        const definition = getConnectorDefinition(type);
        return {
          type,
          name: definition?.name ?? type,
          description: definition?.description ?? '',
          status: statuses[type] ?? 'disabled',
          links: externalIds.filter((link) => link.connectorType === type),
        };
      }),
    );
  }, [itemId, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Integrations"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] flex items-center gap-2">
              <Plug className="h-4 w-4 text-[var(--text-ter)]" />
              Integrations
            </h3>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">
              Link this item to external tools and sync status bidirectionally.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close integrations"
            className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Roster — one row per integration, link out only when it is usable */}
        {rows === null ? (
          <div className="flex items-center gap-2 py-6 text-sm text-[var(--text-ter)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking which integrations are configured…
          </div>
        ) : (
          <ul className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)] border border-[var(--rule)]">
            {rows.map((row) => (
              <IntegrationRosterRow key={row.type} row={row} />
            ))}
          </ul>
        )}

        {/* Link / sync panels — the full contents of the retired Integrations tab */}
        <div className="mt-5 space-y-4">
          <ItemJiraSyncPanel itemId={itemId} itemTitle={itemTitle} />
          <ItemLinearSyncPanel itemId={itemId} itemTitle={itemTitle} />
          <ItemQuantiveSyncPanel itemId={itemId} itemTitle={itemTitle} />
          <ItemGranolaSyncPanel itemId={itemId} itemTitle={itemTitle} />
          <ItemFigmaSyncPanel itemId={itemId} itemTitle={itemTitle} />
        </div>
      </div>
    </div>
  );
}

function IntegrationRosterRow({ row }: { row: IntegrationRow }) {
  const configured = row.status === 'active';
  const openable = configured ? row.links.filter((l) => !!l.externalUrl) : [];

  return (
    <li className="flex items-start justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-[var(--text-pri)]">{row.name}</p>
        {row.description && (
          <p className="text-xs text-[var(--text-ter)] truncate">{row.description}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-1.5 flex-shrink-0">
        {openable.length > 0 ? (
          openable.map((link) => (
            <a
              key={`${link.connectorType}:${link.externalId}`}
              href={link.externalUrl!}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open ${link.externalLabel ?? link.externalId} in ${row.name}`}
              className="inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {link.externalLabel ?? link.externalId}
            </a>
          ))
        ) : configured ? (
          <span className="inline-flex items-center rounded px-2.5 py-1 text-xs font-medium text-[var(--text-ter)] border border-dashed border-[var(--rule)]">
            Connected — not linked to this item
          </span>
        ) : (
          <>
            <span className="inline-flex items-center rounded px-2.5 py-1 text-xs font-medium text-[var(--text-ter)] border border-dashed border-[var(--rule)]">
              {row.status === 'available' ? 'Not connected' : 'Not configured'}
            </span>
            <Link
              to="/prioritization/settings/connectors"
              className="inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              Set up
            </Link>
          </>
        )}
      </div>
    </li>
  );
}

export default ItemIntegrationsModal;
